import * as Sentry from '@sentry/react';

import type { SessionUser } from '@/types/session/SessionUser';
import type { ErrorEvent, EventHint } from '@sentry/react';
import type { AxiosError, AxiosResponse } from 'axios';

import { SENTRY_DSN, ENVIRONMENT } from '@/env';

type CaptureErrorParams = {
    error: Error;
    context?: {
        tags?: Record<string, string>;
        extra?: Record<string, unknown>;
        level?: Sentry.SeverityLevel;
    };
};

/** Marks this app on every event so Performance and Engagement stay separable in one project. */
const APP_TAG = 'performance';

/**
 * How an event is grouped in the dashboard. Kept identical to the Engagement SPA's categories so a
 * single filter on `error.category` reads the same on both sides of the page.
 */
export const ErrorCategory = {
    Api: 'api',
    ChunkLoad: 'chunk-load',
    Network: 'network',
    Render: 'render',
    Unknown: 'unknown',
} as const;

export type ErrorCategoryValue = (typeof ErrorCategory)[keyof typeof ErrorCategory];

const isProduction = ENVIRONMENT === 'production';

/**
 * Messages that are never a fault of this application.
 *
 * - The `Object Not Found Matching Id` family is injected by Outlook SafeLinks and the Edge link
 *   scanner into pages opened from a mail client.
 * - `ResizeObserver loop` is a benign browser notice that the observer needed another frame.
 * - The dynamic-import and chunk failures are the signature of a deploy: a browser still holding the
 *   previous entry point asks for hashed chunks that no longer exist. The host reloads the page,
 *   so recording these would only measure how often we ship.
 */
const IGNORED_ERRORS: (string | RegExp)[] = [
    /Object Not Found Matching Id/i,
    /ResizeObserver loop/i,
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /error loading dynamically imported module/i,
    /Loading (CSS )?chunk \S+ failed/i,
    'ChunkLoadError',
    /Non-Error promise rejection captured with value: (undefined|null|RESTRICTED|CANCELLED)/i,
];

/** Frames that belong to code we neither ship nor control. */
const DENY_URLS: RegExp[] = [
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
    /extensions\//i,
    /googletagmanager\.com/i,
    /google-analytics\.com/i,
    /connect\.facebook\.net/i,
    /widget\.intercom\.io/i,
    /js\.intercomcdn\.com/i,
];

const CHUNK_LOAD_PATTERNS =
    /(Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading (CSS )?chunk|ChunkLoadError|Unable to preload CSS)/i;

type LooseError = {
    name?: string;
    message?: string;
    code?: string;
    /** Axios stamps this on both `Cancel` and `CanceledError`. */
    __CANCEL__?: boolean;
    isDemoRestriction?: boolean;
    config?: { method?: string };
    response?: { status?: number };
};

const asLooseError = (hint?: EventHint): LooseError | undefined => {
    const original = hint?.originalException;
    return original && typeof original === 'object' ? (original as LooseError) : undefined;
};

const readEventMessage = (event: ErrorEvent, error?: LooseError): string =>
    [error?.message, event.message, event.exception?.values?.[0]?.value]
        .filter(Boolean)
        .join(' | ');

/**
 * A request the caller deliberately dropped: a superseded query, a component that unmounted, or a
 * write blocked for a demo account. These arrive in bulk and mean nothing went wrong.
 */
const isCancellation = (error?: LooseError, message = ''): boolean => {
    if (!error) return /CanceledError|AbortError|\bcanceled\b|\bCANCELLED\b/i.test(message);
    return (
        error.__CANCEL__ === true ||
        error.isDemoRestriction === true ||
        error.code === 'ERR_CANCELED' ||
        error.name === 'CanceledError' ||
        error.name === 'AbortError' ||
        /^(canceled|cancelled|RESTRICTED)$/i.test(error.message ?? '')
    );
};

const isChunkLoadFailure = (message: string): boolean => CHUNK_LOAD_PATTERNS.test(message);

/**
 * `ignoreErrors` is applied by the SDK before `beforeSend` runs, so in production these never reach
 * the gate below. It is repeated here so the drop rules hold on their own.
 */
const matchesIgnoredPattern = (message: string): boolean =>
    IGNORED_ERRORS.some((pattern) =>
        typeof pattern === 'string' ? message.includes(pattern) : pattern.test(message),
    );

/**
 * The browser lost its connection. Nothing here can act on it, and it arrives in bursts from a
 * single user on a bad network, so it is kept only when the browser still claims to be online.
 */
const isOfflineNetworkError = (error?: LooseError): boolean =>
    error?.code === 'ERR_NETWORK' && typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * Axios' own phrasing, read when the thrown value did not survive as an object — a rejection that
 * crossed a boundary and arrived as a bare message still names the status it failed with.
 */
const STATUS_IN_MESSAGE = /status code (\d{3})/i;

const readStatus = (error?: LooseError, message = ''): number | undefined => {
    if (error?.response?.status) return error.response.status;

    const fromMessage = STATUS_IN_MESSAGE.exec(message)?.[1];
    return fromMessage ? Number(fromMessage) : undefined;
};

/**
 * Statuses that never carry a defect, whichever path threw them.
 *
 * - `401` is an expired session: the interceptor clears storage and redirects to logout.
 * - `409` is a real concurrent edit, and the one caller that can meet it already says so in its own
 *   words (`useSaveThematicMeeting`).
 * - `426` means the bundle is older than the deployed API. Reloading is the intended recovery, so
 *   recording it would only measure how often we ship.
 */
const NEVER_A_DEFECT: ReadonlySet<number> = new Set([401, 409, 426]);

/** Methods that only read, so a refusal describes the caller's reach rather than what we rendered. */
const READ_METHODS: ReadonlySet<string> = new Set(['get', 'head', 'options']);

const isReadRequest = (error?: LooseError): boolean =>
    READ_METHODS.has((error?.config?.method ?? 'get').toLowerCase());

/**
 * `403` and `404` are only expected on a read: a right the caller lacks, or a resource the server
 * answers as if it did not exist. On a write they are ours — a refused mutation means the interface
 * offered an action it should have hidden, and a `404` on a delete means a stale cache or a wrong
 * id. Those are kept.
 */
const isExpectedRefusal = (status: number | undefined, error?: LooseError): boolean =>
    (status === 403 || status === 404) && isReadRequest(error);

const categorise = (error: LooseError | undefined, message: string): ErrorCategoryValue => {
    if (isChunkLoadFailure(message)) return ErrorCategory.ChunkLoad;
    if (error?.code === 'ERR_NETWORK') return ErrorCategory.Network;
    if (error?.response || error?.config) return ErrorCategory.Api;
    return ErrorCategory.Unknown;
};

/**
 * The last gate before an event leaves the browser. `ignoreErrors` only matches text, while this
 * also sees the thrown value, which is the only way to recognise an aborted request or a refusal.
 *
 * `useError` drops some of the same statuses earlier, because it also decides whether to raise a
 * toast. This gate is the backstop for every other path into the SDK — an unhandled rejection, a
 * direct `captureError`, a throw that no hook saw.
 *
 * Exported so the drop rules can be tested without starting the SDK.
 */
export const beforeSend = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
    const error = asLooseError(hint);
    const message = readEventMessage(event, error);
    const status = readStatus(error, message);

    if (isCancellation(error, message)) return null;
    if (isChunkLoadFailure(message)) return null;
    if (matchesIgnoredPattern(message)) return null;
    if (isOfflineNetworkError(error)) return null;
    if (status !== undefined && NEVER_A_DEFECT.has(status)) return null;
    if (isExpectedRefusal(status, error)) return null;

    event.tags = {
        ...event.tags,
        app: APP_TAG,
        'error.category': categorise(error, message),
        ...(status ? { 'http.status': String(status) } : {}),
    };

    return event;
};

/** Console output would otherwise carry whole API payloads into Sentry as breadcrumbs. */
const beforeBreadcrumb = (breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null => {
    if (
        breadcrumb.category === 'console' &&
        !['warning', 'error'].includes(breadcrumb.level ?? '')
    ) {
        return null;
    }
    return breadcrumb;
};

export const sentryConfigs = {
    dsn: SENTRY_DSN,
    /**
     * Off, so the SDK does not attach the request headers, cookies and IP address it would
     * otherwise infer. `setSentryUser` states exactly what identity an event carries instead.
     */
    sendDefaultPii: false,
    integrations: [
        Sentry.browserTracingIntegration(),
        /**
         * Stated rather than left to the SDK's defaults, which today happen to match. A replay of
         * this product records evaluation answers and written feedback being typed, so the masking
         * it relies on must not be able to change under us on a version bump.
         */
        Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Full tracing in production would spend the quota on healthy page loads.
    tracesSampleRate: isProduction ? 0.1 : 0.5,
    // Replays are kept for what actually failed, plus a slice of plain sessions for context.
    replaysSessionSampleRate: isProduction ? 0.05 : 0,
    replaysOnErrorSampleRate: 1.0,
    environment: ENVIRONMENT,
    ignoreErrors: IGNORED_ERRORS,
    denyUrls: DENY_URLS,
    beforeSend,
    beforeBreadcrumb,
    initialScope: { tags: { app: APP_TAG } },
};

export const sentryInit = () => {
    if (import.meta.env.MODE === 'test' || !SENTRY_DSN) {
        console.warn('Sentry DSN not configured, skipping initialization');
        return;
    }

    /**
     * Inside the Engagement host this module federation bundle shares one document with the host,
     * which initialises Sentry before React mounts. The SDK keeps a single current client per page:
     * a second `init` here would take the global error and rejection handlers away from the host and
     * reroute its events to this project, and a second Session Replay recorder is not supported at
     * all. Standing down leaves the host client in charge, and every helper below keeps working
     * because they all talk to whichever client is current.
     */
    if (Sentry.getClient()) return;

    Sentry.init(sentryConfigs);
};

/**
 * Who hit the issue, named by identifier alone.
 *
 * The address and the display name are deliberately absent: the id resolves to the person in our
 * own directory in seconds, so sending them would place employee contact details in a third-party
 * store to save a lookup. The company is kept as a tag because which client is affected steers
 * triage, and it is not personal data.
 */
export const setSentryUser = (user?: SessionUser) => {
    if (!user) {
        Sentry.setUser(null);
        Sentry.setTags({ companyId: 'unknown', companyName: 'unknown' });
        return;
    }

    Sentry.setUser({ id: user.userId });

    Sentry.setTags({
        companyId: user.companyId,
        companyName: user.companyName ?? 'unknown',
    });
};

export const addApiRequestBreadcrumb = (response: AxiosResponse) => {
    Sentry.addBreadcrumb({
        category: 'api-request',
        message: `${response.config.method?.toUpperCase()} ${response.config.url}`,
        level: 'info',
        data: {
            method: response.config.method?.toUpperCase(),
            url: response.config.url,
            status: response.status,
        },
    });
};

/**
 * The refusal's stable code, read off whichever body shape the server answered with.
 *
 * An allow-list of one scalar, deliberately: the body itself carries evaluation answers, written
 * feedback and employee names, and a breadcrumb is attached to every event the session raises
 * afterwards — so sending the payload would spread this user's data across unrelated issues.
 */
const readRefusalCode = (error: AxiosError): string | undefined => {
    const body = error.response?.data;

    if (!body || typeof body !== 'object') return undefined;

    const { code } = body as { code?: unknown };
    return typeof code === 'string' ? code : undefined;
};

/** Exported so what the breadcrumb carries can be asserted without starting the SDK. */
export const buildApiErrorBreadcrumb = (error: AxiosError): Sentry.Breadcrumb => {
    const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
    const url = error.config?.url || 'UNKNOWN';
    const statusCode = error.response?.status ?? 0;
    const code = readRefusalCode(error);

    return {
        category: 'api-error',
        message: `${method} ${url} - ${statusCode}`,
        level: 'error',
        data: {
            method,
            url,
            statusCode,
            ...(code ? { code } : {}),
            // Axios' own sentence ("Request failed with status code 500", "Network Error"), which
            // names the failure without quoting anything the server sent back.
            reason: error.message,
        },
    };
};

export const addApiErrorBreadcrumb = (error: AxiosError) => {
    Sentry.addBreadcrumb(buildApiErrorBreadcrumb(error));
};

/**
 * A key the dictionary does not know. Left as a breadcrumb rather than an event: the caller falls
 * back to its own wording, so nothing is broken — but the trail is there when something else is.
 */
export const addMissingTranslationKeyBreadcrumb = (key: string) => {
    Sentry.addBreadcrumb({
        category: 'missing-translation-key',
        message: key,
        level: 'warning',
        data: { key },
    });
};

export const captureError = ({ error, context }: CaptureErrorParams) => {
    const looseError = error as LooseError | undefined;
    const message = looseError?.message ?? '';

    // The same gate as `beforeSend`, applied before the event is built: a cancelled request and a
    // stale chunk are both expected, and `useError` is the busiest caller of this helper.
    if (isCancellation(looseError, message) || isChunkLoadFailure(message)) return;

    Sentry.captureException(error, {
        ...context,
        tags: {
            app: APP_TAG,
            'error.category': categorise(looseError, message),
            ...context?.tags,
        },
    });
};
